import * as React from 'react';
import { PagedListingState, BaseRouteProps } from '../../interfaces';
import { User, PagedData } from '../../models';
import { UserDataService } from '../../services';
import { NavLink } from 'react-router-dom';
import { PagedDataGrid, PVAPage } from '..';
import { UserStore, FilterStore } from '../../stores';
import { Roles } from '../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';
import { LoadingIndicatorMini } from '../loadingIndicator/LoadingIndicatorMini';

interface State extends PagedListingState<User> {
    siteWide: boolean;
    filter?: string;
    loading: boolean;
}

interface UserListingProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class UserListing extends React.Component<UserListingProps, State> {
    private userDataService = new UserDataService(this.props.notificationService);
    private orgId: number;

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userOrganization() {
        return this.props.UserStore!.userInfo.organization;
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get organizationName() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization.name;
        }
        return '';
    }

    @computed get hasAccessToSiteWide() {
        return this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin]);
    }

    constructor(props: UserListingProps) {
        super(props);

        this.orgId = 0;

        this.state = {
            entities: [],
            pageProperties: {
                currentPage: 1,
                pageSize: 20,
            },
            sortProperty: {
                id: 'Name',
                sortAscending: true
            },
            siteWide: false,
            filter: '',
            loading: false,
        };

        this.dataFunctionWrapper = this.dataFunctionWrapper.bind(this);
    }

    componentWillReact() {
        if (this.organization && this.organization.id !== this.orgId) {
            this.orgId = this.organization.id;
            // this.setState({
            //     entities: []
            // });
        }
    }

    private dataFunctionWrapper(page: number, newPage: number, pageSize: number, filter?: string, sortPropertyId?: string, sortDirection?: string) {
        const { siteWide } = this.state;
        // sortProperty.id = sortPropertyId || 'Name';
        // sortProperty.sortAscending = sortDirection === 'asc' ? true : false;
        this.setState({
            loading: true
        });

        if (siteWide) {
            return this.userDataService.getPagedLight(page, pageSize, filter, sortPropertyId, sortDirection).then((response: PagedData<User>) => {
                this.updateState(newPage, response);
            });
        } else {
            return this.userDataService.getByOrganizationPaged(this.organization!, page, pageSize, filter, sortPropertyId, sortDirection).then((response: PagedData<User>) => {
                this.updateState(newPage, response);
            });
        }
    }

    private updateState(pageNumber: number, data: PagedData<any>) {
        this.setState({
            entities: data.entities,
            pageProperties: Object.assign(
                {},
                this.state.pageProperties,
                {
                    currentPage: pageNumber,
                    recordCount: data.recordCount
                }
            ),
            loading: false
        });
    }

    impersonateUser = (id: number): any => {
        let entity = this.state.entities.find(x => x.id === id);

        if (entity) {
            const shouldContinue = confirm(`Are you sure you want to impersonate ${entity.firstName} ${entity.lastName}?`);

            if (shouldContinue) {
                this.userDataService.startImpersonation(id);
            }
        }
    }

    private canImpersonate(role: string) {
        if (this.props.UserStore!.checkUserRole(role)) {
            return false;
        }

        var roleInd = User.userRoles.indexOf(role);
        var userRoleInd = User.userRoles.indexOf(this.props.UserStore!.userRole);

        return roleInd > userRoleInd;
    }

    setSiteWide = () => {
        const { siteWide, pageProperties } = this.state;
        this.setState(
            {
                siteWide: !siteWide
            }, 
            () => {
                this.dataFunctionWrapper(0, 0, pageProperties.pageSize || 20);
            });
    }

    public render() {
        const { sortProperty, pageProperties, entities, siteWide, loading } = this.state;

        const listData = entities.map((user: User) => {
            return {
                'Name': `${user.lastName}, ${user.firstName}`,
                'Email': user.email,
                'UserRole': user.userRole,
                'Organization': user.organization ? user.organization.name : '',
                'Language': user.language ? user.language.name : '',
                'ActionKey': { userId: user.id, userRole: user.userRole }
            };
        });

        const actionsColumn = ({ value }: any) => {
            const userId = value.get('userId');
            const userRole = value.get('userRole');
            return (
                <div className="buttons-row">
                    <NavLink to={`/user/${userId}`} className="btn btn-default">View</NavLink>
                    { this.canImpersonate(userRole) && <button className="btn btn-careful" onClick={() => this.impersonateUser(userId)}><span className="glyphicon glyphicon-sunglasses" /></button> }
                </div >
            );
        };

        const columnDefs = [
            { id: 'Name' },
            { id: 'Email' },
            { id: 'UserRole', title: 'Role' },
            { id: 'Organization' },
            { id: 'Language' },
            { id: 'ActionKey', title: 'Actions', width: '220px', customComponent: actionsColumn, sortable: false }
        ];

        return (
            <PVAPage>
                {this.organization && <div id={`organization-${this.organization.id}`} />}

                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            <div>
                                {!siteWide && <><span>Users </span><span className="light">for </span> '{this.organizationName}'</>}
                            </div>
                            {siteWide && <span>All Users</span>}
                            {loading && <LoadingIndicatorMini />}
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                    <PVAPage.Header.Buttons>
                        {this.hasAccessToSiteWide && <button className="btn btn-default" onClick={this.setSiteWide} >
                            {siteWide && <span>Organization</span>}
                            {!siteWide && <span>All Users</span>}
                        </button>}
                        <NavLink className="btn btn-primary" to={'/user/create'}>
                            <span className="glyphicon glyphicon-plus" /> Create
                        </NavLink>
                    </PVAPage.Header.Buttons>
                </PVAPage.Header>

                <PVAPage.Body>
                    {this.organization && <PagedDataGrid
                        key={this.orgId}
                        entities={listData}
                        columns={columnDefs}
                        dataFunction={this.dataFunctionWrapper}
                        defaultSort={sortProperty}
                        pageProperties={pageProperties}
                    />}
                </PVAPage.Body>
                <PVAPage.Footer />
            </PVAPage>);
    }
}


// WEBPACK FOOTER //
// ./src/components/user/UserListing.tsx